import type { EvalRunStep } from '@queries/evalRuns';

import type { EvalCheckResult, EvalCheckVerdict } from '../types';

/**
 * Builds the checklist coverage register(s) shown in the drawer from the run's
 * audit steps.
 *
 * Two things happen here that the raw step rows don't give us:
 *
 *  1. **Batches are merged back together.** The runner splits a checklist pack
 *     into ≤10-check Bedrock calls purely to keep the model's attention per
 *     check constant — an implementation detail. Reviewers think in terms of
 *     "the client checklist", not "batch 2 of 5", so we re-join the batches
 *     into one register per pack.
 *  2. **Problems sort first.** For an audit the question is "what failed /
 *     what couldn't be evidenced", so those rows lead and the passes trail
 *     as the reassurance pile.
 */

/** Reading order in the register: problems first, reassurance last. */
export const VERDICT_GROUP_ORDER: EvalCheckVerdict[] = ['FAIL', 'NOT_EVIDENCED', 'PASS', 'NOT_APPLICABLE'];

export type ChecklistRegister = {
  /** Stable key for the merged pack (step key with any batch suffix removed). */
  packKey: string;
  /** Display name with the "(2/5)" batch suffix stripped. */
  packName: string;
  results: EvalCheckResult[];
  counts: Record<EvalCheckVerdict, number>;
  /** Checks that received a verdict (i.e. everything in `results`). */
  assessed: number;
  /**
   * True when every contributing step succeeded and reported no coverage
   * problem. A step whose verdicts didn't cover its batch records the gap in
   * `error_message`, which is exactly the case the register must not hide.
   * A merely PENDING/RUNNING step sets `isInProgress` instead — an in-flight
   * audit is not a coverage break and must not render as one.
   */
  isComplete: boolean;
  /** True while any contributing step is still PENDING/RUNNING. */
  isInProgress: boolean;
  /** Coverage/step problems worth surfacing above the groups. */
  warnings: string[];
};

const AUDIT_STEP_PREFIX = 'audit#';

/** `audit#client-checklist#2` → `audit#client-checklist` (batch suffix dropped). */
const toPackKey = (stepKey: string): string => stepKey.replace(/#\d+$/, '');

/** `Client operating checklist (2/5)` → `Client operating checklist`. */
const toPackName = (stepLabel: string): string => stepLabel.replace(/\s*\(\d+\/\d+\)\s*$/, '').trim();

const emptyCounts = (): Record<EvalCheckVerdict, number> => ({
  PASS: 0,
  FAIL: 0,
  NOT_EVIDENCED: 0,
  NOT_APPLICABLE: 0,
});

export const buildChecklistRegisters = (steps: EvalRunStep[]): ChecklistRegister[] => {
  const byPack = new Map<string, ChecklistRegister>();

  for (const step of steps) {
    if (!step.step_key.startsWith(AUDIT_STEP_PREFIX)) continue;

    const packKey = toPackKey(step.step_key);
    if (!byPack.has(packKey)) {
      byPack.set(packKey, {
        packKey,
        packName: toPackName(step.step_label),
        results: [],
        counts: emptyCounts(),
        assessed: 0,
        isComplete: true,
        isInProgress: false,
        warnings: [],
      });
    }
    const register = byPack.get(packKey)!;

    for (const result of step.check_results ?? []) {
      register.results.push(result);
      register.counts[result.verdict] += 1;
      register.assessed += 1;
    }

    // A failed step means checks in that batch were never assessed; a
    // succeeded step with an error_message means the model's verdicts didn't
    // cover its batch. Both break the "every check was assessed" guarantee
    // that is the whole point of the audit, so both surface.
    if (step.status === 'FAILED') {
      register.isComplete = false;
      register.warnings.push(`${step.step_label}: step failed — its checks were not assessed.`);
    } else if (step.error_message) {
      register.isComplete = false;
      register.warnings.push(`${step.step_label}: ${step.error_message}`);
    } else if (step.status !== 'SUCCEEDED') {
      register.isInProgress = true;
    }
  }

  // Problems float to the top of each register; within a verdict the audit
  // order is kept (stable sort), which follows the checklist's own order.
  for (const register of byPack.values()) {
    register.results.sort((a, b) => VERDICT_GROUP_ORDER.indexOf(a.verdict) - VERDICT_GROUP_ORDER.indexOf(b.verdict));
  }

  return [...byPack.values()];
};
